import { OrderHelper } from "./OrderHelper"
import { BluetoothPrinter } from "./lib/BluetoothPrinter"
import { HtmlRasterizer } from "./lib/HtmlRasterizer"
import { WindowsAgentClient } from "./lib/WindowsAgentClient"

export class Print {
	_order
	_store
	_completeTaskAction
	_taskBtn
	_onFailed

	/**
	 * @param {*} onFailed Called when nothing was printed and the task was not completed, so the caller can allow a retry
	 */
	constructor(order, store, completeTaskAction, taskBtn, onFailed) {
		this._order = order
		this._store = store
		this._completeTaskAction = completeTaskAction
		this._taskBtn = taskBtn
		this._onFailed = onFailed
	}

	_failed = () => {
		if (this._onFailed) {
			this._onFailed()
		}
	}

	print = () => {
		const isWindows = navigator.userAgent && navigator.userAgent.toLowerCase().includes('windows')

		if (isWindows) {
			// Prefer the local Windows agent (dialog-free print), fall back to the
			// browser print dialog on machines where it isn't installed/running.
			WindowsAgentClient.probe()
				.then(hasAgent => hasAgent ? this._printViaAgent() : this._printViaBrowserDialog())
		}
		else {
			this._printViaBluetooth()
		}
	}

	_printViaBrowserDialog = () => {
		const receiptWidthMm = '72'
		const fontHeightMm = 3.5 // Font size on 80mm roll
		const css = OrderHelper.getCss(receiptWidthMm + 'mm', fontHeightMm + 'mm')
		const innerHtml = OrderHelper.getHtml(this._order, this._store)

		// Use a hidden IFrame to print on Windows
		const iFrameHtml = `
				<html style="margin: 0; padding: 0;">
					<head>
						<style>
							${css}
						</style>
					</head>
					<body style="margin: 0; padding: 0;">
						<div class="receipt">${innerHtml}</div>
						<script>
							addEventListener('afterprint', () => parent.setPrinted())
							document.getElementsByTagName('img')[0].addEventListener('load', () => window.print())
						</script>
					</body>
				</html>
			`

			let iFrameElement = document.createElement('iframe')
			iFrameElement = document.getElementsByTagName('body')[0].appendChild(iFrameElement)
			// Hide iFrame
			iFrameElement.setAttribute('style', 'position: fixed; top: 0; opacity: 0; z-index: -99;')
			// afterprint hadler for iFrame
			window.setPrinted = () => {
				console.debug('Closing iFrame after print')
				iFrameElement.remove()
				if (this._completeTaskAction) {
					this._completeTaskAction(this._taskBtn)
				}
				this.close()
			}
			// Fill iFrame
			iFrameElement.src = "about:blank";
			iFrameElement.contentWindow.document.open();
			iFrameElement.contentWindow.document.write(iFrameHtml);
			iFrameElement.contentWindow.document.close();
	}

	_printViaBluetooth = () => {
		const receiptWidthPx = 384 // Width on 58mm roll
		const fontHeightPx = 23 // Font size on 58mm roll
		const css = OrderHelper.getCss(receiptWidthPx + 'px', fontHeightPx + 'px')
		const innerHtml = OrderHelper.getHtml(this._order, this._store)

		// Render a preview in hidden div to get receipt height
		const probeElement = document.getElementsByTagName('body')[0].appendChild(document.createElement('div'))
		probeElement.innerHTML = `
			<style>
				${css}
			</style>
			<div class="receipt">${innerHtml}</div>
		`
		const probeImg = probeElement.querySelector('img')
		const doPrint = () => {
			const receiptHeightPx = probeElement.querySelector('.receipt').offsetHeight
			probeElement.remove()

			// Use RawBT to print on Android
			const bluetoothPrinter = new BluetoothPrinter()
			bluetoothPrinter.renderRawBtUrl(css, innerHtml, receiptWidthPx, receiptHeightPx)
				.then(rawBtUrl => {
					// Create an anchor and click it with JavaScript
					const confirmationTargetElement = document.body.appendChild(document.createElement('div'))
					confirmationTargetElement.innerHTML = `<a id="printAnchor" href="${rawBtUrl}">.</a>`
					const printAnchor = document.getElementById('printAnchor')
					try {
						printAnchor.click()
						if (this._completeTaskAction) {
							this._completeTaskAction(this._taskBtn)
						}
						confirmationTargetElement.remove()
					}
					catch (e) {
						console.error(e)
						alert('Failed to perform click action for print')
						this._failed()
					}
				})
				.catch(e => {
					console.error('Failed to render receipt for RawBT', e)
					this._failed()
				})
		}
		if (probeImg) {
			probeImg.addEventListener('load', doPrint)
			probeImg.addEventListener('error', () => {
				console.warn('Receipt logo failed to load, printing without waiting for image')
				doPrint()
			})
		} else {
			doPrint()
		}
	}

	/**
	 * Dialog-free print via the local Windows agent: rasterizes the receipt to a PNG
	 * bitmap (same approach as _printViaBluetooth) and posts it to the agent, which
	 * draws it directly onto the configured Windows printer.
	 */
	_printViaAgent = () => {
		const receiptWidthPx = 384 // Width on 58mm roll
		const fontHeightPx = 23 // Font size on 58mm roll
		const css = OrderHelper.getCss(receiptWidthPx + 'px', fontHeightPx + 'px')
		const innerHtml = OrderHelper.getHtml(this._order, this._store)

		// Render a preview in hidden div to get receipt height, same as _printViaBluetooth
		const probeElement = document.getElementsByTagName('body')[0].appendChild(document.createElement('div'))
		probeElement.innerHTML = `
			<style>
				${css}
			</style>
			<div class="receipt">${innerHtml}</div>
		`
		const probeImg = probeElement.querySelector('img')
		const doPrint = () => {
			const receiptHeightPx = probeElement.querySelector('.receipt').offsetHeight
			probeElement.remove()

			const html = `<html style="margin: 0; padding: 0;">
					<head><style>${css}</style></head>
					<body style="margin: 0; padding: 0;"><div class="receipt">${innerHtml}</div></body>
				</html>`
			HtmlRasterizer.renderToCanvas(html, receiptWidthPx, receiptHeightPx)
				.then(canvas => {
					const imageDataUrl = canvas.toDataURL('image/png')
					canvas.remove()
					return WindowsAgentClient.printBitmap(imageDataUrl, receiptWidthPx, receiptHeightPx)
				})
				.then(printed => {
					if (printed) {
						if (this._completeTaskAction) {
							this._completeTaskAction(this._taskBtn)
						}
					}
					else {
						console.warn('Windows agent rejected the print job, falling back to browser print dialog')
						this._printViaBrowserDialog()
					}
				})
				.catch(e => {
					console.error('Failed to print via Windows agent, falling back to browser print dialog', e)
					this._printViaBrowserDialog()
				})
		}
		if (probeImg) {
			probeImg.addEventListener('load', doPrint)
			probeImg.addEventListener('error', () => {
				console.warn('Receipt logo failed to load, printing without waiting for image')
				doPrint()
			})
		} else {
			doPrint()
		}
	}
}